import React, { Fragment } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CurrencyInput,
  IntegerInput,
  NumericInput,
  PercentInput,
  NumericInputProps,
  UnlimitedCurrencyInput,
} from '@/components/NumericInput';
import {
  PolicyTypeSelectDisplay,
  PolicyTypeComboboxOption,
} from '@/components/PolicyTypeSelectDisplay';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/Combobox';
import { FundingSourceForm } from '@/components/FundingSourceForm';
import { BenefitsUtilizedInflationWarning } from '@/components/BenefitsUtilizedInflationWarning';

import { useSelector } from 'react-redux';
import { usePolicyInputFormContext } from '@/components/PolicyInputForm';

import { selectClient, selectFeatureFlags, selectSessionAdvisor } from '@client/model';

import { getApplicablePolicyTypes } from '@client/util';
import {
  MONEY_GUARD_DAILY_BENEFIT_FIELDS,
  isPolicyAddedViaShopInsurance,
  isShopInsuranceMoneyGuard,
  restoreIndependentLivingOnIndemnityFlip,
  scaleMoneyGuardBenefitFields,
} from '@/util/policyFundingSourceUtils';
import { possessive,
  FundingPolicyType,
  fundingPolicyTypeDefList,
  getCoupleLtcStartData,
  isMembershipProgram,
  isCareMattersFamily,
  honorsRetroactiveWaitingPeriodElection,
} from '@shared';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { isIndependentLivingBenefitZeroedOut } from '@shared/calculations/funding/projection/sharedPolicyConfig';
import { getAssetCareDerivedPoolDollars } from '@/util/assetCareDerivedPools';

const INFLATION_MESSAGES = {
  GENERAL_DISABLED:
    'Separate inflation is active. General inflation is disabled. Please fill both Face and COB inflation fields.',
  FACE_DISABLED: 'General inflation applies to all. Face inflation is disabled.',
  COB_DISABLED: 'General inflation applies to all. COB inflation is disabled.',
} as const;

type PolicyFieldKey = keyof PolicyFundingSource;

// sourceQuote can be a parsed object or a JSON string at runtime (see
// FundingTable.tsx, use-pui-cta.ts for the same pattern). The `in` operator
// throws on strings, so normalize before reading.
function readQuoteBenefitDurationMonths(
  sourceQuote: PolicyFundingSource['sourceQuote'],
): number | undefined {
  if (!sourceQuote) return undefined;
  let quote: unknown = sourceQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return undefined;
    }
  }
  if (quote && typeof quote === 'object' && 'ltcBenefitDurationMonths' in quote) {
    const value = (quote as { ltcBenefitDurationMonths?: unknown }).ltcBenefitDurationMonths;
    return typeof value === 'number' ? value : undefined;
  }
  return undefined;
}

/**
 * Recover the original `'Monthly' | 'Annually'` billing frequency from the
 * funding source's source quote. The persisted policy only stores
 * `policyPremiumMonthlyCost` (annual quotes are divided by 12 at quote-import
 * time), so without this the edit form would always say "Monthly Premium" —
 * misleading for advisors editing an annually-billed policy.
 */
function readQuotePremiumFrequency(
  sourceQuote: PolicyFundingSource['sourceQuote'],
): 'Monthly' | 'Annually' | undefined {
  if (!sourceQuote) return undefined;
  let quote: unknown = sourceQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return undefined;
    }
  }
  if (quote && typeof quote === 'object' && 'premiumFrequency' in quote) {
    const value = (quote as { premiumFrequency?: unknown }).premiumFrequency;
    if (value === 'Monthly' || value === 'Annually') return value;
  }
  return undefined;
}

export function InsuranceFormFields() {
  const { policyState } = usePolicyInputFormContext();
  const { policyType } = policyState;
  const client = useSelector(selectClient);
  const {
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  if (!policyType) {
    return <PolicyTypeField />;
  }

  const policyRelatedFields = getPolicyFormFields(policyType);

  return (
    <Fragment>
      <PolicyTypeField />
      {clientHasStartedLtc && <BenefitUtilizedToDateField />}
      {policyRelatedFields.map((Field, index) => (
        <Field key={index} />
      ))}
    </Fragment>
  );
}

const hybridPolicyFields = [
  WaitingPeriodDaysFieldGroup,
  FaceAmountField,
  MinimumGuaranteedBenefitAmountField,
  ContinuationOfBenefitsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  LimitedPayYearsField,
  LumpSumPaymentField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  InflationProtectionOnFaceAmountField,
  InflationProtectionOnCOBAmountField,
  IsSimpleInflationProtectionField,
  InflationProtectionYearsField,
  IsIndemnityPolicyPaymentField,
];

// CareMatters II / Together: the LTC pool is `base monthly ×
// benefit period months` (not a separate COB amount), the face amount
// is flat, and there's a single inflation rate that drives the rider
// only. So: keep FaceAmountField for the death benefit, surface
// BenefitPeriodMonthsField for the LTC pool sizing, drop the COB
// amount and the per-field inflation overrides.
const careMattersFields = [
  WaitingPeriodDaysFieldGroup,
  FaceAmountField,
  MinimumGuaranteedBenefitAmountField,
  BenefitPeriodMonthsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  LimitedPayYearsField,
  LumpSumPaymentField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  IsSimpleInflationProtectionField,
  InflationProtectionYearsField,
  IsIndemnityPolicyPaymentField,
];

// Asset Care sizes its AOB / COB pools as `monthly benefit × benefit period`
// (like Asset Flex), so the two duration fields must be editable. Mirror the
// Asset Flex ordering (durations right after the guaranteed amount, before the
// legacy COB dollar amount) while keeping Asset Care's hybrid extras and the
// cash-indemnity percent.
const assetCareFields = [
  WaitingPeriodDaysFieldGroup,
  FaceAmountField,
  MinimumGuaranteedBenefitAmountField,
  AccelerationBenefitDurationMonthsField,
  EobBenefitDurationMonthsField,
  // Kept visible for legacy policies whose duration fields are still null;
  // ignored by the strategy once the COB/EOB Duration is filled.
  ContinuationOfBenefitsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  LimitedPayYearsField,
  LumpSumPaymentField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  InflationProtectionOnFaceAmountField,
  InflationProtectionOnCOBAmountField,
  IsSimpleInflationProtectionField,
  InflationProtectionYearsField,
  IsIndemnityPolicyPaymentField,
  IndemnityBenefitPercentField,
];

// Securian SecureCare III / IV size BOTH pools as `monthly benefit × benefit
// duration` (see durationPoolModel's all-or-nothing gate), so the two durations
// are the real inputs and the COB dollar amount is not one: it is a product of
// them. Showing an independently editable COB AMOUNT here presented a number the
// projection never reads, so the durations replace it rather than sit beside it.
// Nationwide CareMatters Annuity (single life and joint share one set — the only
// difference is the 24- vs 36-month acceleration window, which the strategy
// defaults from the policy type and this field can override).
const careMattersAnnuityFields = [
  WaitingPeriodDaysFieldGroup,
  CareMattersAnnuityContractValueFields,
  BenefitPeriodMonthsField,
  AccelerationBenefitDurationMonthsField,
  PremiumStartYearField,
  LumpSumPaymentField,
  InflationProtectionField,
  IsSimpleInflationProtectionField,
  InflationProtectionYearsField,
];

const secureCareFields = [
  WaitingPeriodDaysFieldGroup,
  FaceAmountField,
  MinimumGuaranteedBenefitAmountField,
  AccelerationBenefitDurationMonthsField,
  EobBenefitDurationMonthsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  LimitedPayYearsField,
  LumpSumPaymentField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  InflationProtectionOnFaceAmountField,
  InflationProtectionOnCOBAmountField,
  IsSimpleInflationProtectionField,
  InflationProtectionYearsField,
  IsIndemnityPolicyPaymentField,
];

// ManhattanLife OmniFlex Short-Term Care — day-denominated (no dollar pool, no
// death benefit), so there is no Max Benefit dollar field: the benefit periods
// (90/180/270/360 days = 3/6/9/12 months) and the per-setting daily benefits
// are the sizing inputs. The Home Health Care rider is "almost a separate
// policy": its own daily maximum, elimination period, AND benefit period
// (HomeCareBenefitPeriodMonthsField) that drains independently of the
// facility one. The Simple Inflation rider is signaled by a non-zero
// Inflation Protection percent (the engine applies the carrier's accrued-
// benefit curve, not the entered rate), and the Fast-50 election toggles the
// 50%-benefit / no-elimination-period payout mode. Always indemnity — no
// payment-type toggle. See manhattanLifeStcStrategy.
const manhattanLifeStcFields = [
  WaitingPeriodDaysFieldGroup,
  BenefitPeriodMonthsField,
  HomeCareBenefitPeriodMonthsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  IsFast50ElectionField,
];

const policyTypeFormFieldMap: Partial<Record<FundingPolicyType, React.FC[]>> = {
  [FundingPolicyType.longTermCareInsurance]: [
    WaitingPeriodDaysFieldGroup,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    PremiumIncreaseYearsField,
    LimitedPayYearsField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.shortTermCareInsurance]: [
    BenefitPeriodMonthsField,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    PremiumIncreaseYearsField,
    LimitedPayYearsField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.lifeInsuranceWithRider]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountField,
    MinimumGuaranteedBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.hybridLifeInsurance]: hybridPolicyFields,
  // Asset Care's cash indemnity pays a fixed percent of the monthly benefit
  // limit (75% per the carrier — see oa-asset-care/field-mapping.md). The
  // percent was upload-only before; surface it for manual entry so
  // hand-entered indemnity policies don't silently fall back (P2-10).
  [FundingPolicyType.assetCare]: assetCareFields,
  [FundingPolicyType.jointAssetCare]: assetCareFields,
  // Asset Flex previously fell through to hybridPolicyFields. The duration-
  // derived model (see strategies/nylAssetFlexStrategy.ts) needs the two new
  // duration fields surfaced explicitly, and Asset Flex is always
  // reimbursement so the indemnity toggle is omitted.
  [FundingPolicyType.nylAssetFlex]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountField,
    MinimumGuaranteedBenefitAmountField,
    AccelerationBenefitDurationMonthsField,
    EobBenefitDurationMonthsField,
    // Kept visible for legacy policies whose duration fields are still null;
    // ignored by the strategy once EOB Duration is filled.
    ContinuationOfBenefitsField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    PremiumIncreaseYearsField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionField,
    InflationProtectionOnFaceAmountField,
    InflationProtectionOnCOBAmountField,
    IsSimpleInflationProtectionField,
    InflationProtectionYearsField,
  ],
  [FundingPolicyType.careMattersTwo]: careMattersFields,
  [FundingPolicyType.jointCareMatters]: careMattersFields,
  [FundingPolicyType.annuityHybrid]: [
    WaitingPeriodDaysFieldGroup,
    TotalLTCBalanceAtAgeField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.careMattersAnnuity]: careMattersAnnuityFields,
  [FundingPolicyType.jointCareMattersAnnuity]: careMattersAnnuityFields,
  [FundingPolicyType.forecareFixedAnnuity]: [
    WaitingPeriodDaysFieldGroup,
    ForecareGuaranteedRateModelFields,
    AccelerationBenefitDurationMonthsField,
    EobBenefitDurationMonthsField,
    TotalLTCBalanceAtAgeField,
    MinimumGuaranteedBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
  ],
  [FundingPolicyType.indexedAnnuityCare]: [
    WaitingPeriodDaysFieldGroup,
    IndexedAnnuityCareGuaranteedValuesFields,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
  ],
  [FundingPolicyType.lifeWithIndexAccount]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountAtAgeField,
    MinimumGuaranteedBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    DailyMaxBenefitsFieldGroup,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.jointHybridAnnuityBased]: [
    PrimaryPolicyHolderField,
    WaitingPeriodDaysFieldGroup,
    JointTotalLTCBalanceAtAgeField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.hybridLifeIra]: [
    WaitingPeriodDaysFieldGroup,
    FaceAmountAtAgeField,
    ContinuationOfBenefitsField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    PolicyMonthlyWithdrawalRate,
    InflationProtectionOnFaceAmountField,
    InflationProtectionOnCOBAmountField,
    IsSimpleInflationProtectionField,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.jointHybridLifeIra]: [
    WaitingPeriodDaysFieldGroup,
    PrimaryPolicyHolderField,
    JointDeathBenefitAtAgeField,
    ContinuationOfBenefitsField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    LimitedPayYearsField,
    LumpSumPaymentField,
    DailyMaxBenefitsFieldGroup,
    PolicyMonthlyWithdrawalRate,
    InflationProtectionOnFaceAmountField,
    InflationProtectionOnCOBAmountField,
    IsSimpleInflationProtectionField,
    InflationProtectionYearsField,
    IsIndemnityPolicyPaymentField,
  ],
  [FundingPolicyType.fortifiedLife]: [
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    LumpSumPaymentField,
  ],
  [FundingPolicyType.stayYourWay]: [
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    LumpSumPaymentField,
  ],
  [FundingPolicyType.secureCareThree]: secureCareFields,
  [FundingPolicyType.secureCareFour]: secureCareFields,
  [FundingPolicyType.manhattanLifeOmniFlexStc]: manhattanLifeStcFields,
  [FundingPolicyType.nylSecureCare]: [
    WaitingPeriodDaysFieldGroup,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
  ],
  [FundingPolicyType.nylJointSecureCare]: [
    WaitingPeriodDaysFieldGroup,
    MaximumBenefitAmountField,
    PremiumStartYearField,
    PremiumMonthlyCostField,
    PremiumIncreaseRateField,
    DailyMaxBenefitsFieldGroup,
    InflationProtectionFieldGroup,
    InflationProtectionYearsField,
  ],
};

export const getPolicyFormFields = (policyType: FundingPolicyType): React.FC[] => {
  // If the policy type exists in policyTypeFormFieldMap, return its fields
  if (policyTypeFormFieldMap[policyType]) {
    return policyTypeFormFieldMap[policyType]!;
  }

  // Find the policy definition in fundingPolicyTypeDefList
  const policyDef = fundingPolicyTypeDefList.find(def => def.value === policyType);

  // If the policy type is missing, check for its genericType and return those fields
  if (policyDef?.genericType && policyTypeFormFieldMap[policyDef.genericType]) {
    return policyTypeFormFieldMap[policyDef.genericType]!;
  }

  // If the policy type is missing, check for its calculationType and return those fields
  if (policyDef && policyTypeFormFieldMap[policyDef.calculationType]) {
    return policyTypeFormFieldMap[policyDef.calculationType]!;
  }

  // If neither is found, return an empty array
  return [];
};

type InsuranceFormFieldProps = {
  policyFieldKey: PolicyFieldKey;
  label: string;
  InputField: typeof NumericInput;
  placeholder: string;
  unit?: string;
  convertFromDaily?: (dailyValue: number) => number;
  convertToDaily?: (inputValue: number) => number;
  // When a number, the field is shown read-only with this derived value (e.g.
  // an Asset Care pool sized as monthly benefit × benefit period). Null/omitted
  // keeps the field as a normal editable input.
  derivedValue?: number | null;
};

const isViewOnly = (policyType: FundingPolicyType | null) =>
  policyType === FundingPolicyType.allstateHybridPolicy ||
  policyType === FundingPolicyType.agisGroupPolicy;

function getInflationProtectionFieldStates(policyState: PolicyFundingSource) {
  const generalInflation = policyState.policyInflationProtection;
  const faceInflation = policyState.policyInflationProtectionOnFaceAmount;
  const cobInflation = policyState.policyInflationProtectionOnCOBAmount;

  const isGeneralFilled = generalInflation != null;
  const isFaceFilled = faceInflation != null;
  const isCobFilled = cobInflation != null;
  const isFaceOrCobFilled = isFaceFilled || isCobFilled;

  const general = {
    disabled: isFaceOrCobFilled,
    helperText: isFaceOrCobFilled ? INFLATION_MESSAGES.GENERAL_DISABLED : undefined,
  };

  const face = {
    disabled: isGeneralFilled,
    helperText: isGeneralFilled ? INFLATION_MESSAGES.FACE_DISABLED : undefined,
  };

  const cob = {
    disabled: isGeneralFilled,
    helperText: isGeneralFilled ? INFLATION_MESSAGES.COB_DISABLED : undefined,
  };

  return { general, face, cob };
}

function InsuranceFormNumericField({
  InputField,
  placeholder,
  label,
  policyFieldKey,
  convertFromDaily,
  convertToDaily,
  derivedValue,
  inputProps = {},
}: InsuranceFormFieldProps & { inputProps?: Partial<NumericInputProps> }) {
  const { formErrors, policyState, handleChangePolicyField, readOnly } =
    usePolicyInputFormContext();
  // A derived value reflects what the projection uses (e.g. an Asset Care pool
  // sized as monthly benefit × benefit period), so the field is read-only.
  const isDerived = typeof derivedValue === 'number';
  const disabled = readOnly || isViewOnly(policyState.policyType) || isDerived;

  const fieldValue = policyState[policyFieldKey];
  const numericValue = typeof fieldValue === 'number' ? fieldValue : null;
  const displayValue =
    typeof derivedValue === 'number'
      ? derivedValue
      : convertFromDaily && numericValue
        ? convertFromDaily(numericValue)
        : numericValue;

  function handleChange(value: number | null) {
    const parse =
      convertToDaily && value
        ? (nextValue: PolicyFieldValue) => {
            return typeof nextValue === 'number' && nextValue !== null
              ? convertToDaily(nextValue)
              : nextValue;
          }
        : undefined;

    handleChangePolicyField(policyFieldKey, parse)(value);
  }

  return (
    <FundingSourceForm.NumericField
      fieldError={formErrors[policyFieldKey]}
      InputField={InputField}
      inputProps={{
        label,
        disabled,
        placeholder,
        value: displayValue,
        onChange: handleChange,
        onPreview: handleChange,
        ...inputProps,
      }}
    />
  );
}

type InsuranceFormFieldContainerProps = {
  title: string | React.ReactNode;
  description: string;
  FieldComponent: React.FC;
  example?: string;
  children: React.ReactNode;
  className?: string;
};

function InsuranceFormFieldContainer({
  title,
  description,
  FieldComponent,
  children,
  example,
  className,
}: InsuranceFormFieldContainerProps) {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const policyFields = policyType ? getPolicyFormFields(policyType) : [];
  const isLastField = policyFields[policyFields.length - 1] === FieldComponent;

  return (
    <FundingSourceForm.FieldContainer
      title={title}
      description={description}
      example={example}
      isLastField={isLastField}
      className={className}
    >
      {children}
    </FundingSourceForm.FieldContainer>
  );
}

function WaitingPeriodDaysFieldGroup() {
  const {
    intakeSurvey: { clientHasStartedLtc },
  } = useSelector(selectClient);
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);
  return (
    <>
      <InsuranceFormFieldContainer
        title={
          clientHasStartedLtc
            ? 'Remaining Waiting/Elimination Period'
            : 'Waiting/Elimination Period'
        }
        description={
          clientHasStartedLtc
            ? `Number of days remaining in the elimination period before the policy begins to pay for services.`
            : `Number of days you receive care before the policy begins to pay for services.`
        }
        FieldComponent={WaitingPeriodDaysFieldGroup}
      >
        <InsuranceFormNumericField
          InputField={IntegerInput}
          placeholder="Enter days"
          label="HOME CARE WAITING PERIOD"
          policyFieldKey="homeCareWaitingPeriodDays"
        />
        <InsuranceFormNumericField
          InputField={IntegerInput}
          placeholder="Enter days"
          label="FACILITY CARE WAITING PERIOD"
          policyFieldKey="facilityCareWaitingPeriodDays"
        />
      </InsuranceFormFieldContainer>
      {/*
        Only products whose strategy actually reads the stored election get the
        toggle. SecureCare III/IV and CareMatters Annuity treat the retroactive
        lump as a fixed contractual feature and ignore the column, so offering
        the control there let an advisor pick "Not reimbursed" and see the
        projection ignore them.
      */}
      {honorsRetroactiveWaitingPeriodElection(policyState.policyType) && (
        <InsuranceFormFieldContainer
          title="Waiting Period Reimbursement"
          description="If on, the policy pays served waiting period days as a retroactive lump sum once the elimination period is satisfied."
          FieldComponent={WaitingPeriodDaysFieldGroup}
        >
          <FundingSourceForm.BooleanToggleField
            changeHandler={handleChangePolicyField('hasRetroactiveWaitingPeriodReimbursement')}
            value={policyState.hasRetroactiveWaitingPeriodReimbursement ?? false}
            enabledLabel="Reimbursed"
            disabledLabel="Not reimbursed"
            disabled={disabled}
          />
        </InsuranceFormFieldContainer>
      )}
    </>
  );
}

function PremiumStartYearField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const isMembership = isMembershipProgram(policyType);
  const title = isMembership ? 'Membership Start Year ' : 'Policy Purchase Year';
  const description = isMembership
    ? 'The calendar year when the membership was purchased.'
    : 'The calendar year when the policy was purchased.';

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={PremiumStartYearField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter year"
        label={title.toUpperCase()}
        policyFieldKey="policyPremiumStartYear"
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumMonthlyCostField() {
  const {
    policyState: { policyType, sourceQuote, policyLimitedPayYears },
  } = usePolicyInputFormContext();

  const isMembership = isMembershipProgram(policyType);
  // Display & edit in annual terms when either:
  //   1. The source quote was annually-billed (e.g. CareScout's annual mode) —
  //      the carrier's invoice is yearly and we want the field to match. Save
  //      still writes monthly back (annual ÷ 12) for engine consistency.
  //   2. The policy is a limited-pay product (5/10/20-pay) — conventionally
  //      discussed in annual terms even when the carrier bills monthly.
  const hasAnnualSourceQuote =
    !isMembership && readQuotePremiumFrequency(sourceQuote) === 'Annually';
  const isLimitedPay = !isMembership && (policyLimitedPayYears ?? 0) > 0;
  const isAnnualBilled = hasAnnualSourceQuote || isLimitedPay;

  const title = isMembership
    ? 'Monthly Maintenance Fees'
    : isAnnualBilled
      ? 'Annual Premium'
      : 'Monthly Premium';
  const description = isMembership
    ? 'The amount paid monthly to participate in the program.'
    : isAnnualBilled
      ? 'The amount paid for the policy every year.'
      : 'The amount paid for the policy every month.';

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={PremiumMonthlyCostField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label={title.toUpperCase()}
        policyFieldKey="policyPremiumMonthlyCost"
        // The display↔stored conversion props are named "Daily" historically
        // (used for daily-benefit fields) but the shape is generic — for
        // annually-billed policies we display monthly × 12 and save annual / 12.
        convertFromDaily={isAnnualBilled ? monthly => monthly * 12 : undefined}
        convertToDaily={isAnnualBilled ? annual => annual / 12 : undefined}
      />
    </InsuranceFormFieldContainer>
  );
}

function LimitedPayYearsField() {
  return (
    <InsuranceFormFieldContainer
      title="Limited Pay Period"
      description="Number of years premium payments are required for limited pay policies. Leave blank if N/A."
      FieldComponent={LimitedPayYearsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter years"
        label="LIMITED PAY YEARS"
        policyFieldKey="policyLimitedPayYears"
        unit="years"
      />
    </InsuranceFormFieldContainer>
  );
}

function LumpSumPaymentField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  const isMembership = isMembershipProgram(policyType);
  const title = isMembership ? 'Entrance Fee' : 'Lump-Sum Payment';
  const description = isMembership
    ? 'Fee required to enter the program. Provide the total amount, not the installment amount.'
    : 'A one-time premium payment made towards the policy. Leave blank if not applicable.';

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={LumpSumPaymentField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label={title.toUpperCase()}
        policyFieldKey="policyLumpSumPayment"
      />
    </InsuranceFormFieldContainer>
  );
}

function IsIndemnityPolicyPaymentField() {
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);
  const shouldScaleOnToggle = isShopInsuranceMoneyGuard(policyState);

  const handleToggleIndemnity = (value: PolicyFieldValue) => {
    const nextIsIndemnity = value as boolean;
    const currentIsIndemnity = policyState.isIndemnityPolicyPayment ?? false;
    if (shouldScaleOnToggle && nextIsIndemnity !== currentIsIndemnity) {
      const restoredIl = restoreIndependentLivingOnIndemnityFlip(policyState, nextIsIndemnity);
      const policyForScaling =
        restoredIl !== undefined
          ? { ...policyState, dailyIndependentLivingBenefitAmount: restoredIl }
          : policyState;
      const scaled = scaleMoneyGuardBenefitFields(policyForScaling, nextIsIndemnity);
      for (const key of MONEY_GUARD_DAILY_BENEFIT_FIELDS) {
        const nextValue = scaled[key];
        if (typeof nextValue === 'number') {
          handleChangePolicyField(key)(nextValue);
        }
      }
    }
    handleChangePolicyField('isIndemnityPolicyPayment')(nextIsIndemnity);
  };

  const description = shouldScaleOnToggle
    ? 'The type of payment the policy provides. Indemnity benefits pay 80% of the contractual daily maximum, so toggling will recalculate the daily benefit amounts accordingly.'
    : 'The type of payment the policy provides.';

  return (
    <InsuranceFormFieldContainer
      title="Payment Type"
      description={description}
      FieldComponent={IsIndemnityPolicyPaymentField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleToggleIndemnity}
        disabled={disabled}
        value={policyState.isIndemnityPolicyPayment}
        enabledLabel="Indemnity"
        disabledLabel="Reimbursement"
      />
    </InsuranceFormFieldContainer>
  );
}

// Asset Care cash indemnity pays a fixed percent of the monthly benefit limit
// (75% per the carrier; the engine defaults a missing/non-positive value to
// 75% — P2-10). Rendered only when indemnity is elected.
function IndemnityBenefitPercentField() {
  const { policyState } = usePolicyInputFormContext();
  if (policyState.isIndemnityPolicyPayment !== true) return null;

  return (
    <InsuranceFormFieldContainer
      title="Indemnity Benefit Percent"
      description="The percent of the monthly benefit limit paid as cash indemnity. Asset Care caps cash indemnity at 75% of the monthly maximum — leave blank (or 0) to use the 75% product default."
      FieldComponent={IndemnityBenefitPercentField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="75%"
        label="INDEMNITY BENEFIT PERCENT"
        policyFieldKey="policyIndemnityBenefitPercent"
      />
    </InsuranceFormFieldContainer>
  );
}

// ManhattanLife OmniFlex Fast-50™ election. Fast-50 is an alternative payout
// mode: 50% of the accrued daily benefit with no elimination period, for care
// from family or friends (no licensed-care requirement). It is either/or with
// the full indemnity payout, and Fast-50 days do not satisfy the elimination
// period — see manhattanLifeStcStrategy.
function IsFast50ElectionField() {
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);
  return (
    <InsuranceFormFieldContainer
      title="Model Fast-50 Election (Informal Care)"
      description="Fast-50 pays 50% of the accrued daily benefit with no elimination period, for care from family or friends. It is mutually exclusive with full indemnity payouts, and Fast-50 days do not satisfy the elimination period."
      FieldComponent={IsFast50ElectionField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleChangePolicyField('isFast50Election')}
        value={policyState.isFast50Election ?? false}
        enabledLabel="Fast-50 (50% benefit)"
        disabledLabel="Full indemnity"
        disabled={disabled}
      />
    </InsuranceFormFieldContainer>
  );
}

// Joint ShareCare products (NYL SecureCare joint, NGL EssentialLTC joint)
// store the COMBINED household maximum: the calc splits it into thirds
// (client individual / partner individual / shared pool), so a per-insured
// figure entered here would understate coverage 3×. See the joint ShareCare
// model comment in traditionalLtcStrategy.ts.
const isJointShareCarePolicyType = (policyType: FundingPolicyType | null) =>
  policyType === FundingPolicyType.nylJointSecureCare ||
  policyType === FundingPolicyType.jointNglEssentialLtc;

function MaximumBenefitAmountField() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();
  const isJointShareCare = isJointShareCarePolicyType(policyType);

  return (
    <InsuranceFormFieldContainer
      title="Max Policy Benefit"
      description={
        isJointShareCare
          ? `This is the COMBINED household total for both spouses —
             the sum of each spouse's lifetime maximum plus the
             shared-care pool (typically each spouse's individual
             maximum multiplied by 3). Do not enter one spouse's
             individual maximum; the calculation splits this combined
             amount into each spouse's pool plus the shared pool. The
             amount will be inflated by the chosen inflation
             protection`
          : `This is the maximum dollar amount that will be
                    paid out for long-term care under this policy.
                    Refer to your policy for how your Max Policy
                    Benefit is calculated. For most policies, you
                    multiply your daily benefit amount by the total
                    number of days in the benefit period. The amount
                    will be inflated by the chosen inflation
                    protection`
      }
      example={
        isJointShareCare
          ? `Example: If each spouse has a $250,000 lifetime maximum
             with a shared-care rider, the combined household total
             is 3 x 250,000 = $750,000.`
          : `Example: For a 5-year benefit period with a $200
                daily benefit, the maximum benefit would be 5 x
                365 x 200 = $365,000.`
      }
      FieldComponent={MaximumBenefitAmountField}
    >
      <InsuranceFormNumericField
        InputField={UnlimitedCurrencyInput}
        placeholder="Unlimited"
        label="MAX BENEFIT AMOUNT"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

function TotalLTCBalanceAtAgeField() {
  const {
    clientFirstName,
    intakeSurvey: { clientHasStartedLtc },
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  return (
    <InsuranceFormFieldContainer
      title={`Total LTC Balance at Age ${ltcAtAge}`}
      description={`The expected or guaranteed accumulated value at
                    age ${ltcAtAge}, which is ${possessive(clientFirstName)}
                    ${clientHasStartedLtc ? '' : 'expected  '}start age for long-term care (LTC). This
                    balance is the total amount available to cover
                    qualified LTC expenses.`}
      FieldComponent={TotalLTCBalanceAtAgeField}
    >
      <InsuranceFormNumericField
        InputField={UnlimitedCurrencyInput}
        placeholder="Unlimited"
        label="TOTAL LTC BALANCE"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

type JointBalanceAtAgeFieldProps = {
  policyFieldKey: PolicyFieldKey;
  label: string;
  title: string;
  description: string;
};

function JointBalanceAtAgeField({
  policyFieldKey,
  label,
  title,
  description,
}: JointBalanceAtAgeFieldProps) {
  const client = useSelector(selectClient);
  const { policyState } = usePolicyInputFormContext();
  const { mutableClientPartner, clientFirstName } = client;
  const coupleLtcStartData = getCoupleLtcStartData(client, mutableClientPartner);
  const expectedPolicyHolderAgeAtPolicyActivation = coupleLtcStartData
    ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(policyState.isPrimaryPolicyHolder)
    : null;

  const primaryPolicyHolderName = policyState.isPrimaryPolicyHolder
    ? clientFirstName
    : mutableClientPartner?.clientFirstName;
  const startsLtcFirstPartnerName = coupleLtcStartData?.clientStartsLtcFirst
    ? clientFirstName
    : mutableClientPartner?.clientFirstName;

  return (
    <InsuranceFormFieldContainer
      title={`${title} at Age ${expectedPolicyHolderAgeAtPolicyActivation}`}
      description={description}
      example={`The policy activation age ${expectedPolicyHolderAgeAtPolicyActivation} is determined by two factors.
                1) the primary policy holder, and 2) the
                age at which the first partner is expected to
                start LTC. Since ${primaryPolicyHolderName} is the primary policy holder, the policy activation age is based on ${possessive(primaryPolicyHolderName)} age when the first partner (${startsLtcFirstPartnerName}) is expected to start LTC in year ${coupleLtcStartData?.coupleLtcStartYear}.`}
      FieldComponent={JointTotalLTCBalanceAtAgeField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label={label}
        policyFieldKey={policyFieldKey}
      />
    </InsuranceFormFieldContainer>
  );
}

function JointTotalLTCBalanceAtAgeField() {
  return (
    <JointBalanceAtAgeField
      policyFieldKey="policyMaximumBenefitAmount"
      label="TOTAL LTC BALANCE"
      title="Total LTC Balance"
      description="This balance is the expected or guaranteed
                    accumulated value when the first partner is
                    expected to begin long-term care."
    />
  );
}

function JointDeathBenefitAtAgeField() {
  return (
    <JointBalanceAtAgeField
      policyFieldKey="policyMaximumBenefitAmount"
      label="DEATH BENEFIT"
      title="Face Amount (Death Benefit)"
      description={`The death benefit at the time of the first partner's
                   long-term care start age based on
                   projected growth rates. Unused funds are paid to
                   beneficiaries upon policyholder's death.`}
    />
  );
}

function FaceAmountField() {
  const { policyState } = usePolicyInputFormContext();
  const { policyType } = policyState;
  const { face: derivedFace } = getAssetCareDerivedPoolDollars(policyState);

  // Asset Care's Face Amount IS the Acceleration (AOB) pool — Monthly Benefit ×
  // Acceleration Duration — so it's shown as a derived, read-only value. Asset
  // Flex derives its Acceleration pool the same way internally, but its Face
  // Amount only anchors the (independent) death benefit, so it stays editable.
  let description: string;
  if (derivedFace != null) {
    description =
      'Derived from Monthly Benefit × Acceleration (AOB) Duration — this is the Acceleration pool and the death benefit the projection uses. Edit the Monthly Benefit or AOB Duration to change it.';
  } else if (policyType === FundingPolicyType.nylAssetFlex) {
    description =
      'The life-insurance Face Amount, used as the death-benefit ceiling. The LTC Acceleration pool is derived from the Monthly Benefit × Acceleration Duration.';
  } else {
    description = `This pool is used for qualified LTC expenses. Unused funds are paid to beneficiaries upon policyholder's death. The amount will be inflated by the chosen inflation protection.`;
  }

  return (
    <InsuranceFormFieldContainer
      title="Face Amount (Death Benefit)"
      description={description}
      FieldComponent={FaceAmountField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="FACE AMOUNT"
        policyFieldKey="policyMaximumBenefitAmount"
        derivedValue={derivedFace}
      />
    </InsuranceFormFieldContainer>
  );
}

function FaceAmountAtAgeField() {
  const {
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  return (
    <InsuranceFormFieldContainer
      title={`Face Amount (Death Benefit) at Age ${ltcAtAge}`}
      description={`The death benefit at age ${ltcAtAge} based on
                  projected growth rates. Unused funds are paid to
                    beneficiaries upon policyholder's death.`}
      FieldComponent={FaceAmountAtAgeField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="FACE AMOUNT"
        policyFieldKey="policyMaximumBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

function ContinuationOfBenefitsField() {
  const { policyState } = usePolicyInputFormContext();
  const { policyType } = policyState;
  const { cob: derivedCob } = getAssetCareDerivedPoolDollars(policyState);
  let title;
  let label;
  switch (policyType) {
    case FundingPolicyType.nylAssetFlex:
      title = 'Extension of Benefits Amount';
      label = 'EOB AMOUNT';
      break;
    default:
      title = 'Continuation of Benefits Amount';
      label = 'COB AMOUNT';
      break;
  }

  // Asset Flex and Asset Care model the COB/EOB pool as COB Duration × Monthly
  // Benefit. For Asset Care we show the derived product read-only (the value the
  // projection actually uses). Asset Flex keeps its legacy dollar field editable
  // but flags it as ignored once the duration is set.
  const eobDurationIgnored =
    policyType === FundingPolicyType.nylAssetFlex &&
    policyState.policyEobBenefitDurationMonths != null;
  let description: string;
  if (derivedCob != null) {
    description =
      'Derived from Monthly Benefit × COB Duration — this is the Continuation pool the projection uses. Edit the Monthly Benefit or COB Duration to change it.';
  } else if (eobDurationIgnored) {
    description =
      'Ignored — the EOB Duration above takes precedence. Leave for reference only; the projection engine no longer reads this field when the duration is set.';
  } else {
    description = `This pool is utilized for qualified long-term
                    care expenses after the Face Amount is depleted.
                    Any funds remaining in this pool will not be
                    paid to beneficiaries. The amount will be
                    inflated by the chosen inflation protection.`;
  }

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={ContinuationOfBenefitsField}
    >
      <InsuranceFormNumericField
        InputField={UnlimitedCurrencyInput}
        placeholder="Unlimited"
        label={label}
        policyFieldKey="policyContinuationBenefitAmount"
        derivedValue={derivedCob}
      />
    </InsuranceFormFieldContainer>
  );
}

function AccelerationBenefitDurationMonthsField() {
  return (
    <InsuranceFormFieldContainer
      title="LTC Acceleration Benefit Duration"
      description="The number of months the LTC Acceleration Benefit pool will last at the monthly benefit cap. Found on the Illustration Input Summary."
      example="Example: '2 years' on the illustration = 24 months."
      FieldComponent={AccelerationBenefitDurationMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter months"
        label="ACCELERATION DURATION (MONTHS)"
        policyFieldKey="policyAccelerationBenefitDurationMonths"
        unit="months"
      />
    </InsuranceFormFieldContainer>
  );
}

function EobBenefitDurationMonthsField() {
  return (
    <InsuranceFormFieldContainer
      title="EOB Benefit Duration"
      description="The number of months the Extension of Benefits pool will last at the monthly benefit cap. Leave blank or set to 0 if the EOB rider is not elected."
      example="Example: '4 years' on the illustration = 48 months."
      FieldComponent={EobBenefitDurationMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter months (0 if no EOB)"
        label="EOB DURATION (MONTHS)"
        policyFieldKey="policyEobBenefitDurationMonths"
        unit="months"
      />
    </InsuranceFormFieldContainer>
  );
}

function getIndexedAnnuityCareMonthlyLimit(policyState: PolicyFundingSource): number | null {
  const dailyLimits = MONEY_GUARD_DAILY_BENEFIT_FIELDS.map(key => policyState[key]).filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value),
  );
  if (dailyLimits.length === 0) return null;
  return Math.max(...dailyLimits) * DAYS_IN_MONTH;
}

function IndexedAnnuityCareGuaranteedValuesFields() {
  const {
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  const { formErrors, policyState, handleChangePolicyField, readOnly } =
    usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);
  const guaranteedLtcbb =
    policyState.policyBaseBenefitAmountAtIssue ?? policyState.policyMaximumBenefitAmount ?? null;
  const monthlyLimit = getIndexedAnnuityCareMonthlyLimit(policyState);

  function handleGuaranteedLtcbbChange(value: PolicyFieldValue) {
    handleChangePolicyField('policyBaseBenefitAmountAtIssue')(value);
    handleChangePolicyField('policyMaximumBenefitAmount')(value);
  }

  function handleMonthlyLimitChange(value: PolicyFieldValue) {
    const dailyEquivalent = typeof value === 'number' ? value / DAYS_IN_MONTH : null;
    for (const key of MONEY_GUARD_DAILY_BENEFIT_FIELDS) {
      handleChangePolicyField(key)(dailyEquivalent);
    }
  }

  function handleCobBalanceChange(value: PolicyFieldValue) {
    handleChangePolicyField('policyContinuationBenefitAmountAtIssue')(value);
    handleChangePolicyField('policyContinuationBenefitAmount')(value);
  }

  return (
    <Fragment>
      <InsuranceFormFieldContainer
        title={`Guaranteed Rates LTC Values at Age ${ltcAtAge}`}
        description="Values from the Account Value Summary Based on Guaranteed Rates row for expected LTC start. The monthly withdrawal limit is stored as daily equivalents for projection calculations."
        FieldComponent={IndexedAnnuityCareGuaranteedValuesFields}
      >
        <FundingSourceForm.NumericField
          fieldError={
            formErrors.policyBaseBenefitAmountAtIssue || formErrors.policyMaximumBenefitAmount
          }
          InputField={CurrencyInput}
          inputProps={{
            label: 'GUARANTEED LTC BENEFIT BALANCE',
            disabled,
            placeholder: 'Enter dollars',
            value: guaranteedLtcbb,
            onChange: handleGuaranteedLtcbbChange,
            onPreview: handleGuaranteedLtcbbChange,
          }}
        />
        <FundingSourceForm.NumericField
          fieldError={
            formErrors.dailyHomeBenefitAmount ||
            formErrors.dailyIndependentLivingBenefitAmount ||
            formErrors.dailyAssistedLivingBenefitAmount ||
            formErrors.dailyNursingHomeBenefitAmount
          }
          InputField={CurrencyInput}
          inputProps={{
            label: 'LTC MONTHLY WITHDRAWAL LIMIT',
            disabled,
            placeholder: 'Enter monthly dollars',
            value: monthlyLimit,
            onChange: handleMonthlyLimitChange,
            onPreview: handleMonthlyLimitChange,
          }}
        />
        <InsuranceFormNumericField
          InputField={IntegerInput}
          placeholder="Enter months"
          label="BASE BENEFIT PERIOD"
          policyFieldKey="policyAccelerationBenefitDurationMonths"
          unit="months"
        />
      </InsuranceFormFieldContainer>

      <InsuranceFormFieldContainer
        title="COB Rider"
        description="COB is a separate LTC pool used only after the base LTC Benefit Balance is exhausted. Set the balance to 0 if the rider is not elected."
        FieldComponent={IndexedAnnuityCareGuaranteedValuesFields}
      >
        <FundingSourceForm.NumericField
          fieldError={
            formErrors.policyContinuationBenefitAmountAtIssue ||
            formErrors.policyContinuationBenefitAmount
          }
          InputField={CurrencyInput}
          inputProps={{
            label: 'COB RIDER LTC BALANCE',
            disabled,
            placeholder: 'Enter dollars',
            value:
              policyState.policyContinuationBenefitAmountAtIssue ??
              policyState.policyContinuationBenefitAmount ??
              null,
            onChange: handleCobBalanceChange,
            onPreview: handleCobBalanceChange,
          }}
        />
        <InsuranceFormNumericField
          InputField={IntegerInput}
          placeholder="Enter months"
          label="COB BENEFIT PERIOD"
          policyFieldKey="policyEobBenefitDurationMonths"
          unit="months"
        />
        <InsuranceFormNumericField
          InputField={PercentInput}
          placeholder="Enter %"
          label="COB INFLATION RATE"
          policyFieldKey="policyInflationProtectionOnCOBAmount"
        />
        <InsuranceFormNumericField
          InputField={IntegerInput}
          placeholder="Leave blank if uncapped"
          label="COB INFLATION YEARS"
          policyFieldKey="policyInflationProtectionYears"
          unit="years"
        />
      </InsuranceFormFieldContainer>
    </Fragment>
  );
}

function ForecareGuaranteedRateModelFields() {
  return (
    <Fragment>
      <InsuranceFormFieldContainer
        title="ForeCare Guaranteed Year-1 Values"
        description="Guaranteed no-rider values from the ForeCare illustration used to seed Contract Value and Extended Benefit layers."
        FieldComponent={ForecareGuaranteedRateModelFields}
      >
        <InsuranceFormNumericField
          InputField={CurrencyInput}
          placeholder="Enter dollars"
          label="ACCELERATED / CONTRACT VALUE"
          policyFieldKey="policyBaseBenefitAmountAtIssue"
        />
        <InsuranceFormNumericField
          InputField={CurrencyInput}
          placeholder="Enter dollars"
          label="MAXIMUM EXTENDED BENEFIT"
          policyFieldKey="policyContinuationBenefitAmountAtIssue"
        />
        <InsuranceFormNumericField
          InputField={CurrencyInput}
          placeholder="Enter dollars"
          label="CONTRACT VALUE / DEATH BENEFIT"
          policyFieldKey="policyAccumulatedValueAtIssue"
        />
      </InsuranceFormFieldContainer>

      <InsuranceFormFieldContainer
        title="ForeCare Guaranteed Contract Value Schedule"
        description="Guaranteed Contract Value rates and LTC rider charge used to reproduce the guaranteed no-rider illustration ledger."
        FieldComponent={ForecareGuaranteedRateModelFields}
      >
        <InsuranceFormNumericField
          InputField={PercentInput}
          placeholder="Enter %"
          label="INITIAL GUARANTEED RATE"
          policyFieldKey="policyBaseBenefitGuaranteedInitialRate"
        />
        <InsuranceFormNumericField
          InputField={IntegerInput}
          placeholder="Enter years"
          label="INITIAL RATE THROUGH YEAR"
          policyFieldKey="policyBaseBenefitGuaranteedInitialRateYears"
          unit="years"
        />
        <InsuranceFormNumericField
          InputField={PercentInput}
          placeholder="Enter %"
          label="RENEWAL GUARANTEED RATE"
          policyFieldKey="policyBaseBenefitGuaranteedRenewalRate"
        />
        <InsuranceFormNumericField
          InputField={NumericInput}
          placeholder="Enter rate"
          label="LTC RIDER MONTHLY CHARGE"
          policyFieldKey="policyMonthlyContinuationBenefitChargeRate"
        />
      </InsuranceFormFieldContainer>
    </Fragment>
  );
}

// Nationwide CareMatters Annuity. The contract value is the ONE input everything
// else derives from: the monthly cap is that value over the acceleration window,
// the acceleration pool IS that value, and the death benefit is whatever of it
// long-term care does not consume. See
// @shared/calculations/docs/nationwide-carematters-annuity/README.md.
//
// Deliberately NOT offered here:
//   - indemnity vs reimbursement. Always cash indemnity (illustration p.4), a
//     product property rather than an election, so the strategy gates on policy
//     type and an editable field could only ever be wrong.
//   - risk class. Not stored on a policy, and not needed: the benefit period
//     over the acceleration window gives the LTC multiple, which IS the class,
//     which sets the rider charge rate.
function CareMattersAnnuityContractValueFields() {
  return (
    <Fragment>
      <InsuranceFormFieldContainer
        title="Contract Value"
        description="The annuity account value at issue. It is the single premium less any inflation-protection or nonforfeiture rider charge, so a policy with riders starts well below the premium paid."
        example="Example: $100,000 premium with no riders = $100,000. With 5% Compound elected = $52,000."
        FieldComponent={CareMattersAnnuityContractValueFields}
      >
        <InsuranceFormNumericField
          InputField={CurrencyInput}
          placeholder="Enter dollars"
          label="CONTRACT VALUE"
          policyFieldKey="policyAccumulatedValueAtIssue"
        />
      </InsuranceFormFieldContainer>

      <InsuranceFormFieldContainer
        title="Guaranteed Interest Rate"
        description="The guaranteed rate credited to the contract value each year. Leave blank to use the product's guaranteed 3.00%."
        FieldComponent={CareMattersAnnuityContractValueFields}
      >
        <InsuranceFormNumericField
          InputField={PercentInput}
          placeholder="Enter a percentage"
          label="GUARANTEED INTEREST RATE"
          policyFieldKey="policyAccumulatedValueGuaranteedInitialRate"
        />
      </InsuranceFormFieldContainer>
    </Fragment>
  );
}

function BenefitPeriodMonthsField() {
  const { policyState } = usePolicyInputFormContext();
  const { policyType, sourceQuote } = policyState;
  const quoteBenefitDurationMonths = readQuoteBenefitDurationMonths(sourceQuote);

  let description: string;
  if (isCareMattersFamily(policyType)) {
    const base =
      'The projected benefit duration. We assume the full monthly benefit is taken every month during claim, since taking less in any month forfeits the inflation rider for that month.';
    description = quoteBenefitDurationMonths
      ? `${base} If left blank, the benefit duration from the source quote (${quoteBenefitDurationMonths} months) will be used.`
      : base;
  } else {
    description = 'The maximum length of time your insurance provider will payout benefits. ';
  }

  return (
    <InsuranceFormFieldContainer
      title="Benefit Period Months"
      description={description}
      FieldComponent={BenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter months"
        label="BENEFIT PERIOD MONTHS"
        policyFieldKey="policyBenefitPeriodMonths"
        unit="months"
      />
    </InsuranceFormFieldContainer>
  );
}

// ManhattanLife OmniFlex: the Home Health Care rider carries its OWN benefit
// period, independent of the facility one — home care days draw it down
// separately and cannot borrow facility days. Blank falls back to the
// facility benefit period (pre-field policies), and it only matters when a
// home daily benefit (the rider) is present.
function HomeCareBenefitPeriodMonthsField() {
  return (
    <InsuranceFormFieldContainer
      title="Home Care Benefit Period Months"
      description="The benefit period for the home health care rider, separate from
                  the facility benefit period. Home care days draw down this
                  period independently — they cannot borrow facility days. If
                  left blank, the facility benefit period is assumed."
      FieldComponent={HomeCareBenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter months"
        label="HOME CARE BENEFIT PERIOD MONTHS"
        policyFieldKey="homeCareBenefitPeriodMonths"
        unit="months"
      />
    </InsuranceFormFieldContainer>
  );
}

function MinimumGuaranteedBenefitAmountField() {
  return (
    <InsuranceFormFieldContainer
      title="Minimum Guaranteed Death Benefit"
      description="The minimum (residual) amount that will be paid to
                  beneficiaries upon the policyholder's death
                  regardless of the amount used for LTC expenses.
                  The amount will not be affected by the chosen
                  inflation protection."
      FieldComponent={MinimumGuaranteedBenefitAmountField}
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder="Enter dollars"
        label="MINIMUM DEATH BENEFIT"
        policyFieldKey="policyMinimumGuaranteedBenefitAmount"
      />
    </InsuranceFormFieldContainer>
  );
}

export function BenefitUtilizedToDateField() {
  const { policyState } = usePolicyInputFormContext();
  return (
    <InsuranceFormFieldContainer
      title="Benefit Utilized To Date"
      description="The dollar amount of benefits used to date to pay for long-term care."
      FieldComponent={BenefitUtilizedToDateField}
      // FieldContainer puts each child in its own cell of a 2-column grid and
      // would otherwise cap this one at the left column. `cn` runs twMerge, so
      // this overrides its `md:col-end-2` and lets the cell span both columns —
      // which is what gives the warning full width. The input keeps its original
      // half-width via the inner grid below.
      className="md:col-end-3"
    >
      {/* Input and caveat are ONE child on purpose: as siblings they would land
          in separate grid cells, side by side. Driven by the in-progress
          `policyState`, so switching policy type shows/hides it immediately. */}
      <div className="flex flex-col gap-3">
        {/* Mirrors the parent's `grid-cols-2 gap-x-6` so the input renders at
            exactly the width it had before the warning existed. */}
        <div className="grid gap-x-6 md:grid-cols-2">
          <InsuranceFormNumericField
            InputField={CurrencyInput}
            placeholder="Enter dollars"
            label="BENEFIT UTILIZED TO DATE"
            policyFieldKey="policyBenefitUtilizedToDate"
          />
        </div>
        <BenefitsUtilizedInflationWarning policyType={policyState.policyType} />
      </div>
    </InsuranceFormFieldContainer>
  );
}

export function PolicyMonthlyWithdrawalRate() {
  const {
    policyState: { policyType },
  } = usePolicyInputFormContext();

  let title;
  let description = `The amount limit the policy will cover for each day of service based on the care setting. The amounts will be inflated by the chosen inflation protection.`;

  switch (policyType) {
    case FundingPolicyType.hybridLifeIra:
    case FundingPolicyType.jointHybridLifeIra:
      title = `Monthly Withdrawal Rate for Base Policy`;
      description = `The percentage of the benefit amount that can be withdrawn per month while withdrawing from the base policy.`;
      break;
  }
  return (
    <InsuranceFormFieldContainer
      title={title ?? 'Monthly Withdrawal Rate'}
      description={
        description ?? 'The percentage of the benefit amount that can be withdrawn per month.'
      }
      FieldComponent={PolicyMonthlyWithdrawalRate}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="MONTHLY WITHDRAWAL RATE"
        policyFieldKey="policyMonthlyWithdrawalRate"
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumIncreaseYearsField() {
  return (
    <InsuranceFormFieldContainer
      title="Premium Increase Period"
      description="Number of years the premium will increase. If left blank, the premiums will increase until projected on-claim year."
      FieldComponent={BenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter years"
        label="PREMIUM INCREASE YEARS"
        policyFieldKey="policyPremiumIncreaseYears"
        unit="years"
      />
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionYearsField() {
  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection Period"
      description="Number of years the value of benefits will increase. If left blank, the benefits will increase until projected end of care year."
      FieldComponent={BenefitPeriodMonthsField}
    >
      <InsuranceFormNumericField
        InputField={IntegerInput}
        placeholder="Enter years"
        label="INFLATION PROTECTION PERIOD"
        policyFieldKey="policyInflationProtectionYears"
        unit="years"
      />
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionField() {
  const { policyState } = usePolicyInputFormContext();
  const { general } = getInflationProtectionFieldStates(policyState);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection"
      description="The percentage that all benefit amounts increase to keep up with inflation"
      FieldComponent={InflationProtectionField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION"
        policyFieldKey="policyInflationProtection"
        inputProps={{
          disabled: general.disabled,
        }}
      />
      {general.helperText && (
        <div className="pt-[20px] text-xs text-gray-500">{general.helperText}</div>
      )}
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionOnFaceAmountField() {
  const { policyState } = usePolicyInputFormContext();
  const { face } = getInflationProtectionFieldStates(policyState);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection on Face Amount"
      description="The percentage the face amount increases to keep up with inflation"
      FieldComponent={InflationProtectionOnFaceAmountField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION (FACE AMOUNT)"
        policyFieldKey="policyInflationProtectionOnFaceAmount"
        inputProps={{
          disabled: face.disabled,
        }}
      />
      {face.helperText && <div className="pt-[20px] text-xs text-gray-500">{face.helperText}</div>}
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionOnCOBAmountField() {
  const { policyState } = usePolicyInputFormContext();
  const { cob } = getInflationProtectionFieldStates(policyState);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection on COB Amount"
      description="The percentage the COB amount increases to keep up with inflation"
      FieldComponent={InflationProtectionOnCOBAmountField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION (COB AMOUNT)"
        policyFieldKey="policyInflationProtectionOnCOBAmount"
        inputProps={{
          disabled: cob.disabled,
        }}
      />
      {cob.helperText && <div className="pt-[20px] text-xs text-gray-500">{cob.helperText}</div>}
    </InsuranceFormFieldContainer>
  );
}

const MAX_BENEFIT_INTERVAL = ['daily', 'monthly', 'annual'];

function DailyMaxBenefitsFieldGroup() {
  const client = useSelector(selectClient);
  const {
    appliedInferenceSet: { ltcAtAge },
    mutableClientPartner,
    clientFirstName,
  } = client;
  const {
    policyState: { policyType },
    policyState,
    maxBenefitsInterval,
    handleMaxBenefitsIntervalChange,
    readOnly,
  } = usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);

  const isMoneyGuardFA = policyType === FundingPolicyType.moneyGuardFixedAdvantage;
  const isShopInsurancePolicy = isPolicyAddedViaShopInsurance(policyState);
  const maxFieldsLockedByShopInsurance = isMoneyGuardFA && isShopInsurancePolicy;
  // Mirrors the calc-side carve-out exactly so the form never shows an IL
  // value the projection won't pay (see sharedPolicyConfig.ts).
  const isShopInsuranceReimbursementILZero = isIndependentLivingBenefitZeroedOut(policyState);

  function getConvertFromDaily() {
    if (maxBenefitsInterval === 'daily') {
      return undefined;
    }
    return (daily: number) => convertFromDailyEquivalent(daily, maxBenefitsInterval);
  }

  function getConvertToDaily() {
    if (maxBenefitsInterval === 'daily') {
      return undefined;
    }
    return (input: number) => convertToDailyEquivalent(input, maxBenefitsInterval);
  }

  let title;
  let description = `The amount limit the policy will cover per selected interval based on the care setting. The amounts will be inflated by the chosen inflation protection.`;

  switch (policyType) {
    case FundingPolicyType.annuityHybrid:
      title = `Max Benefits at Age ${ltcAtAge}`;
      break;
    case FundingPolicyType.lifeWithIndexAccount:
      title = `Max Benefits at Age ${ltcAtAge}`;
      description = `The amount limit the policy will cover per selected interval based on the care setting. This amount is based on the death benefit when ${clientFirstName} is ${ltcAtAge} and the withdrawl rate.`;
      break;
    case FundingPolicyType.jointHybridAnnuityBased: {
      const coupleLtcStartData = getCoupleLtcStartData(client, mutableClientPartner);
      const expectedPolicyHolderAgeAtPolicyActivation = coupleLtcStartData
        ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(policyState.isPrimaryPolicyHolder)
        : null;
      title = `Max Benefits at Age ${expectedPolicyHolderAgeAtPolicyActivation}`;
      description = `The amount limit the policy will cover per selected interval based on the care setting. See the "Total LTC Balance" section for details on the policy activation age.`;
      break;
    }
    case FundingPolicyType.jointHybridLifeIra:
    case FundingPolicyType.hybridLifeIra:
      title = `Max Benefits for COB Rider`;
      description = `The initial monthly benefit amount for the COB rider. This is not the monthly amount for the base policy.`;
      break;
  }

  return (
    <InsuranceFormFieldContainer
      title={
        <div className="flex items-center gap-2">
          <span>{title ?? 'Max Benefits'}</span>
          <Select
            value={maxBenefitsInterval}
            onValueChange={handleMaxBenefitsIntervalChange}
            disabled={disabled}
          >
            <SelectTrigger className="h-9 w-max rounded-lg border-gray-200 bg-gray-100 text-sm capitalize text-gray-800">
              <SelectValue
                className="text-sm text-gray-700"
                placeholder="Select One"
              />
            </SelectTrigger>
            <SelectContent
              className="max-h-[200px] overflow-y-auto"
              position="popper"
              sideOffset={4}
            >
              {MAX_BENEFIT_INTERVAL.map(interval => (
                <SelectItem
                  key={interval}
                  value={interval}
                  className="py-2 capitalize"
                >
                  {interval}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      description={description}
      example="Use the unit selector above to enter your benefit amount in the format shown in your policy."
      FieldComponent={DailyMaxBenefitsFieldGroup}
      className="gap-y-4"
    >
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`HOME CARE (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyHomeBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
        inputProps={maxFieldsLockedByShopInsurance ? { disabled: true } : undefined}
      />
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`INDEPENDENT LIVING (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyIndependentLivingBenefitAmount"
        convertFromDaily={isShopInsuranceReimbursementILZero ? () => 0 : getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
        inputProps={
          maxFieldsLockedByShopInsurance || isShopInsuranceReimbursementILZero
            ? { disabled: true }
            : undefined
        }
      />
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`ASSISTED LIVING (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyAssistedLivingBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
        inputProps={maxFieldsLockedByShopInsurance ? { disabled: true } : undefined}
      />
      <InsuranceFormNumericField
        InputField={CurrencyInput}
        placeholder={`Enter ${maxBenefitsInterval} amount`}
        label={`NURSING HOME (${maxBenefitsInterval.toUpperCase()})`}
        policyFieldKey="dailyNursingHomeBenefitAmount"
        convertFromDaily={getConvertFromDaily()}
        convertToDaily={getConvertToDaily()}
        inputProps={maxFieldsLockedByShopInsurance ? { disabled: true } : undefined}
      />
    </InsuranceFormFieldContainer>
  );
}

function IsSimpleInflationProtectionField() {
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);

  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection Type"
      description="Default calculation is compound."
      FieldComponent={IsSimpleInflationProtectionField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleChangePolicyField('isSimpleInflationProtection')}
        value={policyState.isSimpleInflationProtection}
        enabledLabel="Simple"
        disabledLabel="Compound"
        disabled={disabled}
      />
    </InsuranceFormFieldContainer>
  );
}

function InflationProtectionFieldGroup() {
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const disabled = readOnly || isViewOnly(policyState.policyType);
  return (
    <InsuranceFormFieldContainer
      title="Inflation Protection"
      description="The percentage the value of benefits increases to keep up with inflation. Default calculation is compound."
      FieldComponent={InflationProtectionFieldGroup}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="INFLATION PROTECTION"
        policyFieldKey="policyInflationProtection"
      />
      <div>
        <FundingSourceForm.BooleanToggleField
          changeHandler={handleChangePolicyField('isSimpleInflationProtection')}
          value={policyState.isSimpleInflationProtection}
          enabledLabel="Simple"
          disabledLabel="Compound"
          disabled={disabled}
        />
      </div>
    </InsuranceFormFieldContainer>
  );
}

function PrimaryPolicyHolderField() {
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const { mutableClientPartner, clientFullName } = useSelector(selectClient);
  const disabled = readOnly || isViewOnly(policyState.policyType);
  return (
    <InsuranceFormFieldContainer
      title="Primary Policy Holder"
      description={`${clientFullName} is the assumed primary policyholder. If this is incorrect, please deselect the toggle below.`}
      FieldComponent={PrimaryPolicyHolderField}
    >
      <FundingSourceForm.BooleanToggleField
        changeHandler={handleChangePolicyField('isPrimaryPolicyHolder')}
        value={policyState.isPrimaryPolicyHolder ?? false}
        enabledLabel={clientFullName}
        disabledLabel={mutableClientPartner?.clientFullName || 'Partner'}
        disabled={disabled}
      />
    </InsuranceFormFieldContainer>
  );
}

function PremiumIncreaseRateField() {
  const { policyState } = usePolicyInputFormContext();
  const isMembership = isMembershipProgram(policyState.policyType);

  let title = 'Premium Increase Rate';
  let description = 'The annual percentage increase in the premium amount.';
  if (isMembership) {
    title = 'Maintenance Fee Increase Rate';
    description = 'The annual percentage increase in the monthly maintenance fee.';
  }

  return (
    <InsuranceFormFieldContainer
      title={title}
      description={description}
      FieldComponent={LimitedPayYearsField}
    >
      <InsuranceFormNumericField
        InputField={PercentInput}
        placeholder="Enter %"
        label="ANNUAL INCREASE RATE"
        policyFieldKey="policyPremiumIncreaseRate"
      />
    </InsuranceFormFieldContainer>
  );
}

export function PolicyTypeField() {
  const { policyState, handleChangePolicyField, readOnly } = usePolicyInputFormContext();
  const { policyType } = policyState;
  const client = useSelector(selectClient);
  const featureFlags = useSelector(selectFeatureFlags);
  const applicablePolicyTypes = getApplicablePolicyTypes(client, featureFlags, policyType);

  // Create the options array from applicable policy types
  const allPolicyTypeOptions = applicablePolicyTypes.map(policyTypeDef => ({
    value: policyTypeDef.value,
    label: `${policyTypeDef.carrier ? `${policyTypeDef.carrier} ` : ''}${policyTypeDef.label}`,
    selectedDisplay: <PolicyTypeSelectDisplay policyType={policyTypeDef} />,
    optionDisplay: <PolicyTypeComboboxOption policyType={policyTypeDef} />,
  }));

  // Create a new array with the selected policy type first (if it exists)
  const policyTypeOptions = policyType
    ? [
        ...allPolicyTypeOptions.filter(option => option.value === policyType),
        ...allPolicyTypeOptions.filter(option => option.value !== policyType),
      ]
    : allPolicyTypeOptions;

  const { advisorId, advisorEmail, organizationDisplayName } = useSelector(selectSessionAdvisor);
  const requestFormBaseUrl = 'https://waterlily.typeform.com/to/YSsEMouO';
  const requestFormUrl = `${requestFormBaseUrl}#advisor_id=${encodeURIComponent(advisorId)}&advisor_email=${encodeURIComponent(advisorEmail)}&advisor_organization=${organizationDisplayName ? encodeURIComponent(organizationDisplayName) : ''}`;

  const disabled = readOnly || isViewOnly(policyState.policyType);

  return (
    <InsuranceFormFieldContainer
      title="Policy Type"
      description="Your specific long-term care policy or a generic type"
      FieldComponent={PolicyTypeField}
      className="md:col-start-1 md:col-end-4"
    >
      <Combobox
        options={policyTypeOptions}
        value={policyState.policyType}
        onChange={handleChangePolicyField('policyType')}
        className="relative z-[1000] w-full"
        disabled={disabled}
        emptyMessage={
          <div className="flex flex-col gap-1 p-4">
            <a
              href={requestFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button size="sm">Request Policy Type</Button>
            </a>
          </div>
        }
      />
    </InsuranceFormFieldContainer>
  );
}

/**
 * Stable references to the policy-form field components, keyed by name.
 * The projection-details Policy Details card uses these as Map keys so it
 * can show exactly the same fields the input form renders for a given
 * policy type. Keep in sync with `policyTypeFormFieldMap`.
 */
export const FORM_FIELD_COMPONENTS = {
  WaitingPeriodDaysFieldGroup,
  FaceAmountField,
  FaceAmountAtAgeField,
  MaximumBenefitAmountField,
  TotalLTCBalanceAtAgeField,
  JointTotalLTCBalanceAtAgeField,
  JointDeathBenefitAtAgeField,
  MinimumGuaranteedBenefitAmountField,
  ContinuationOfBenefitsField,
  CareMattersAnnuityContractValueFields,
  ForecareGuaranteedRateModelFields,
  IndexedAnnuityCareGuaranteedValuesFields,
  AccelerationBenefitDurationMonthsField,
  EobBenefitDurationMonthsField,
  BenefitPeriodMonthsField,
  HomeCareBenefitPeriodMonthsField,
  PremiumStartYearField,
  PremiumMonthlyCostField,
  PremiumIncreaseRateField,
  PremiumIncreaseYearsField,
  LimitedPayYearsField,
  LumpSumPaymentField,
  DailyMaxBenefitsFieldGroup,
  InflationProtectionField,
  InflationProtectionOnFaceAmountField,
  InflationProtectionOnCOBAmountField,
  IsSimpleInflationProtectionField,
  InflationProtectionFieldGroup,
  InflationProtectionYearsField,
  IsIndemnityPolicyPaymentField,
  IndemnityBenefitPercentField,
  IsFast50ElectionField,
  PrimaryPolicyHolderField,
  PolicyMonthlyWithdrawalRate,
  BenefitUtilizedToDateField,
} as const;

function convertToDailyEquivalent(value: number, interval: string): number {
  switch (interval) {
    case 'monthly':
      return value / 30;
    case 'annual':
      return value / 365;
    default:
      return value;
  }
}

function convertFromDailyEquivalent(dailyValue: number, interval: string): number {
  switch (interval) {
    case 'monthly':
      return dailyValue * 30;
    case 'annual':
      return dailyValue * 365;
    default:
      return dailyValue;
  }
}
